/**
 * Navbar — Shared navigation bar for public pages.
 *
 * Extracted from the Landing page so Landing, Pricing, Privacy,
 * and future legal pages all share the same glassmorphism nav.
 *
 * Auth-aware CTA: shows "Dashboard" when logged in, "Get Started" otherwise.
 * Scroll-spy highlighting on the landing page (Features / How It Works).
 * Active page highlighting for other routes (e.g. Pricing).
 * Tracks nav clicks and CTA interactions via GA4 helpers.
 */

import { useState, useEffect } from 'react';
import { Box, Container, Group, Button } from '@mantine/core';
import { useNavigate, useLocation } from 'react-router-dom';
import { IconArrowRight } from '@tabler/icons-react';
import { HaiveLogo } from './HaiveLogo';
import { useAuthStore } from '../../store/auth';
import { trackNavClick, trackCTAClick } from '../../analytics';

/** Sections on the landing page that the navbar can highlight. */
const LANDING_SECTIONS = ['features', 'how-it-works'] as const;
type LandingSection = (typeof LANDING_SECTIONS)[number];

/**
 * On the landing page, observes which section is in the viewport
 * and returns its id (or null if none is visible).
 */
function useScrollSpy(enabled: boolean): LandingSection | null {
  const [active, setActive] = useState<LandingSection | null>(null);

  useEffect(() => {
    if (!enabled) {
      setActive(null);
      return;
    }

    const observers: IntersectionObserver[] = [];
    const visible = new Map<string, boolean>();

    function setup() {
      for (const id of LANDING_SECTIONS) {
        const el = document.getElementById(id);
        if (!el) return false;

        const observer = new IntersectionObserver(
          ([entry]) => {
            visible.set(id, entry.isIntersecting);
            // Pick the first visible section (top-to-bottom priority)
            const found = LANDING_SECTIONS.find((s) => visible.get(s));
            setActive(found ?? null);
          },
          { threshold: 0.15 },
        );
        observer.observe(el);
        observers.push(observer);
      }
      return true;
    }

    // Sections may not be in the DOM yet (lazy loading) — retry briefly
    if (!setup()) {
      const timer = setTimeout(setup, 300);
      return () => {
        clearTimeout(timer);
        observers.forEach((o) => o.disconnect());
      };
    }

    return () => observers.forEach((o) => o.disconnect());
  }, [enabled]);

  return active;
}

export function Navbar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const isLanding = pathname === '/';
  const isPricing = pathname === '/pricing';

  const activeSection = useScrollSpy(isLanding);

  const handleHashNav = (hash: string, label: string) => {
    trackNavClick(label);
    if (isLanding) {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/#' + hash);
    }
  };

  return (
    <Box
      component="nav"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        background: 'rgba(10,10,11,.8)',
        borderBottom: '1px solid rgba(255,255,255,.05)',
      }}
    >
      <Container size="lg">
        <Group justify="space-between" h={72} align="center">
          <Box style={{ marginTop: -4, cursor: 'pointer' }} onClick={() => navigate('/')}>
            <HaiveLogo h={30} />
          </Box>
          <Group gap="xs">
            <Button
              variant="subtle"
              color={activeSection === 'features' ? 'orange' : 'gray'}
              size="sm"
              visibleFrom="sm"
              style={{ fontWeight: 500 }}
              onClick={() => handleHashNav('features', 'Features')}
            >
              Features
            </Button>
            <Button
              variant="subtle"
              color={activeSection === 'how-it-works' ? 'orange' : 'gray'}
              size="sm"
              visibleFrom="sm"
              style={{ fontWeight: 500 }}
              onClick={() => handleHashNav('how-it-works', 'How It Works')}
            >
              How It Works
            </Button>
            <Button
              variant="subtle"
              color={isPricing ? 'orange' : 'gray'}
              size="sm"
              onClick={() => {
                trackNavClick('Pricing');
                navigate('/pricing');
              }}
              style={{ fontWeight: 500 }}
            >
              Pricing
            </Button>
            {isAuthenticated ? (
              <Button
                size="sm"
                color="haiveOrange"
                radius="xl"
                onClick={() => {
                  trackCTAClick('Dashboard', 'navbar', '/dashboard');
                  navigate('/dashboard');
                }}
                rightSection={<IconArrowRight size={16} />}
              >
                Dashboard
              </Button>
            ) : (
              <Button
                size="sm"
                color="haiveOrange"
                radius="xl"
                onClick={() => {
                  trackCTAClick('Get Started', 'navbar', '/login');
                  navigate('/login');
                }}
                rightSection={<IconArrowRight size={16} />}
              >
                Get Started
              </Button>
            )}
          </Group>
        </Group>
      </Container>
    </Box>
  );
}
